// ==============================
// File: src/LandlordSignIn.jsx
// ==============================
import React, { useState, useRef, useEffect } from "react";
import { Mail, Lock, Eye, EyeOff, Loader2, X } from "lucide-react";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { loginLandlord, sendOtpLandlord, verifyOtp } from "../api/LandlordUser";
import { useNavigate } from "react-router-dom";

import SessionToast from "../components/SessionToast";
import { usePostAuthNotice } from "../hooks/usePostAuthNotice";
import { logTokenExpiry } from "../utils/tokenDebug";

/** ---------- helpers ---------- **/
function persistRememberFlag(remember) {
  if (remember) {
    localStorage.setItem("remember_me", "true");
    sessionStorage.removeItem("remember_me");
  } else {
    localStorage.removeItem("remember_me");
    sessionStorage.setItem("remember_me", "false");
    // 🔒 Make sure no RT lingers
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("refresh_token");
  }
}

function getStore(remember) {
  return remember ? localStorage : sessionStorage;
}

function setDefaultSelectedFacilities(user) {
  try {
    if (!user?.facilities?.length) return;
    const raw = localStorage.getItem("selected_facilities");
    const existing = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(existing) || existing.length === 0) {
      const allIds = user.facilities.map((f) => f.facility_id);
      localStorage.setItem("selected_facilities", JSON.stringify(allIds));
    }
  } catch {}
}

function clearAuthForNewLogin() {
  const KEYS = [
    "access_token",
    "refresh_token",
    "token_type",
    "user",
    "user_email",
    "remember_me",
    "selected_facilities", // avoid leaking previous user's selection
  ];
  [localStorage, sessionStorage].forEach((s) =>
    KEYS.forEach((k) => s.removeItem(k))
  );
}

function storeAuthBundle({
  remember,
  access_token,
  token_type = "Bearer",
  refresh_token,
  user,
  user_email,
}) {
  persistRememberFlag(remember);
  const store = getStore(remember);
  if (access_token) store.setItem("access_token", access_token);
  if (token_type) store.setItem("token_type", token_type);
  if (remember && refresh_token)
    localStorage.setItem("refresh_token", refresh_token);
  if (user) store.setItem("user", JSON.stringify(user));
  if (user_email) store.setItem("user_email", user_email);
  // seed facilities selection so dashboard isn’t empty by default
  setDefaultSelectedFacilities(user);
}

function ResendOtpSection({ email, token }) {
  const [resendIn, setResendIn] = useState(30);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const resendOtp = async () => {
    if (!email || !token) return;
    setResending(true);
    try {
      await sendOtpLandlord({ email, purpose: "registration" }, token);
      setResendIn(30);
    } catch {
      alert("Failed to resend OTP. Try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="mt-4 text-center text-sm text-gray-600">
      {resendIn > 0 ? (
        <p>
          Resend code in <span className="font-medium">{resendIn}s</span>
        </p>
      ) : (
        <button
          type="button"
          onClick={resendOtp}
          disabled={resending}
          className="text-indigo-600 hover:underline disabled:opacity-50"
        >
          {resending ? "Resending..." : "Resend code"}
        </button>
      )}
    </div>
  );
}

export default function LandlordSignIn() {
  const [showPw, setShowPw] = useState(false);
  const { notice, clear } = usePostAuthNotice();

  // OTP state
  const [otpModal, setOtpModal] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpLoading, setOtpLoading] = useState(false);
  const [loginPayload, setLoginPayload] = useState(null);
  const inputsRef = useRef([]);
  const verifyBtnRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const remember = localStorage.getItem("remember_me") === "true";
    const token = localStorage.getItem("access_token");

    // logTokenExpiry();

    if (remember && token) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const openOtpModalFresh = (
    email,
    access_token,
    refresh_token,
    remember_me
  ) => {
    setLoginPayload({ email, access_token, refresh_token, remember_me });
    setOtp(Array(6).fill(""));
    setOtpLoading(false);
    setOtpModal(true);
  };

  useEffect(() => {
    if (!otpModal) return;
    setOtp(Array(6).fill(""));
    const t = setTimeout(() => inputsRef.current[0]?.focus(), 0);
    return () => clearTimeout(t);
  }, [otpModal]);

  const formik = useFormik({
    initialValues: { email: "", password: "", remember_me: false },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string()
        .min(6, "Min 6 chars")
        .required("Password is required"),
    }),
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      setStatus("");

      clearAuthForNewLogin();

      try {
        const res = await loginLandlord({
          email: values.email.trim().toLowerCase(),
          password: values.password,
          remember_me: values.remember_me,
        });

        const {
          access_token,
          token_type = "Bearer",
          refresh_token, // from login response
          user,
          requires_otp,
        } = res?.data || {};

        // ----- OTP flow -----
        if (requires_otp) {
          if (!access_token || !user?.email) {
            setStatus("Login failed: missing token or user email.");
            return;
          }

          // Persist remember flag; only keep RT if remember=true
          persistRememberFlag(values.remember_me);
          if (values.remember_me && refresh_token) {
            localStorage.setItem("refresh_token", refresh_token);
          }

          await sendOtpLandlord(
            { email: user.email, purpose: "registration" },
            access_token
          );

          openOtpModalFresh(
            user.email,
            access_token,
            refresh_token,
            values.remember_me
          );
          return;
        }

        // ----- Normal (no OTP) flow -----
        if (!access_token) {
          setStatus("Login failed: no token returned.");
          return;
        }

        storeAuthBundle({
          remember: values.remember_me,
          access_token,
          token_type,
          refresh_token: values.remember_me ? refresh_token : undefined,
          user,
          user_email: values.email.trim().toLowerCase(),
        });

        // hard reload ensures all views see the tokens immediately
        window.location.replace("/dashboard");
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.response?.data?.error?.message;
        if (Array.isArray(detail)) {
          setStatus(
            detail
              .map((d) => d?.msg)
              .filter(Boolean)
              .join(", ")
          );
        } else {
          setStatus(detail || "Login failed. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ----- OTP handlers -----
  const handleOtpChange = (val, idx) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      const digits = pasted.split("");
      setOtp(digits);
      setTimeout(() => verifyBtnRef.current?.focus(), 0);
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0)
      inputsRef.current[idx - 1]?.focus();
    if (e.key === "Enter") {
      const code = otp.join("");
      if (code.length === 6 && !otpLoading) submitOtp();
    }
  };

  const submitOtp = async () => {
    const code = otp.join("");
    if (code.length !== 6 || !loginPayload) return;

    setOtpLoading(true);
    try {
      const res = await verifyOtp(
        { email: loginPayload.email, otp: code, purpose: "registration" },
        loginPayload.access_token
      );

      const token = res?.data?.access_token || loginPayload.access_token;
      const tokenType = res?.data?.token_type || "Bearer";
      const refreshToken =
        res?.data?.refresh_token || loginPayload?.refresh_token;
      const userData = res?.data?.data;
      const email = loginPayload.email;

      if (!token) {
        alert("Verification failed: no token returned.");
        return;
      }

      storeAuthBundle({
        remember: !!loginPayload?.remember_me,
        access_token: token,
        token_type: tokenType,
        refresh_token: refreshToken,
        user: userData,
        user_email: email,
      });

      setOtpModal(false);
      window.location.replace("/dashboard");
    } catch {
      alert("Invalid OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-100 via-white to-violet-100 p-4">
      {/* 🔔 One-shot session toast if redirected due to session expiry */}
      <SessionToast notice={notice} onClose={clear} autoHideMs={6000} />

      {/* Background blobs */}
      <motion.div
        className="absolute top-[-20%] left-[-10%] w-[400px] h-[400px] rounded-full 
             bg-gradient-to-br from-blue-200 to-blue-400 
             blur-3xl opacity-30"
        animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
        transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
      />

      <motion.div
        className="absolute bottom-[-15%] right-[-5%] w-[350px] h-[350px] rounded-full 
             bg-gradient-to-br from-gray-300 to-gray-400 
             blur-3xl opacity-30"
        animate={{ y: [0, -30, 0], x: [0, -20, 0] }}
        transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
      />

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md bg-gradient-to-br from-white/70 to-white/30 backdrop-blur-2xl shadow-2xl rounded-3xl border border-white/40 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-8 sm:px-10 sm:py-12 text-center">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="
    mx-auto h-16 w-16 rounded-2xl
    bg-gradient-to-br from-blue-600 via-blue-500 to-blue-600
    ring-1 ring-blue-300/40
    text-white grid place-items-center text-3xl shadow-lg
  "
          >
            🅿️
          </motion.div>

          <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Landlord Sign In
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your car parks, facilities & revenue
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={formik.handleSubmit}
          className="px-6 sm:px-10 pb-8 space-y-5"
        >
          {formik.status && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm text-center">
              {formik.status}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                className="w-full rounded-xl border border-gray-200 px-11 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-300 bg-white/70 backdrop-blur"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <p className="mt-1 text-xs text-red-600">{formik.errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPw ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                className="w-full rounded-xl border border-gray-200 px-11 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-300 bg-white/70 backdrop-blur"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
              >
                {showPw ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <div className="text-right mt-1">
              <button
                type="button"
                onClick={() => navigate("/forget-password")}
                className="text-xs text-blue-600 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="remember_me"
              name="remember_me"
              checked={formik.values.remember_me}
              onChange={formik.handleChange}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="remember_me" className="text-sm text-gray-700">
              Remember me
            </label>
          </div>

          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="
  w-full inline-flex justify-center items-center gap-2
  rounded-xl bg-blue-600 hover:bg-blue-700
  text-white font-medium py-3 text-sm
  shadow-lg hover:shadow-xl
  disabled:opacity-50 disabled:cursor-not-allowed
  transition
"
          >
            {formik.isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="px-6 sm:px-10 py-4 bg-white/50 border-t border-white/30 text-xs text-gray-600 text-center backdrop-blur">
          © {new Date().getFullYear()} Landlord Car Park Portal
        </div>
      </motion.div>

      {/* OTP Modal */}
      {otpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOtpModal(false)}
          />
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="
        relative w-full max-w-sm sm:max-w-md 
        bg-gradient-to-br from-white/90 to-white/70 
        backdrop-blur-2xl shadow-2xl rounded-2xl sm:rounded-3xl 
        border border-white/40 
        px-4 py-5 sm:p-8 
        max-h-[90vh] overflow-y-auto
      "
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
              onClick={() => setOtpModal(false)}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Title */}
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 text-center">
              Verify OTP
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600 text-center">
              Enter the 6-digit code sent to your email.
            </p>

            {/* OTP Inputs */}
            <div className="mt-5 sm:mt-6 flex justify-center gap-2.5 sm:gap-3.5 flex-wrap">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputsRef.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, idx)}
                  onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                  onPaste={idx === 0 ? handleOtpPaste : undefined}
                  className="
              w-10 h-10 sm:w-12 sm:h-12 
              text-center text-lg sm:text-xl font-semibold 
              rounded-xl border border-gray-300 
              bg-white/80 backdrop-blur 
              focus:ring-2 focus:ring-blue-300 
              outline-none
            "
                />
              ))}
            </div>

            {/* Resend Section */}
            <div className="mt-4 sm:mt-5">
              <ResendOtpSection
                email={loginPayload?.email}
                token={loginPayload?.access_token}
              />
            </div>

            {/* Verify Button */}
            <button
              ref={verifyBtnRef}
              onClick={submitOtp}
              disabled={otp.join("").length !== 6 || otpLoading}
              className="
  mt-5 sm:mt-6 w-full inline-flex justify-center items-center gap-2
  rounded-xl bg-blue-600 hover:bg-blue-700
  text-white font-medium py-3 text-sm sm:text-base
  shadow-lg hover:shadow-xl
  disabled:opacity-50 disabled:cursor-not-allowed
  transition
"
            >
              {otpLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Verifying...
                </>
              ) : (
                "Verify & Continue"
              )}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
